import { getAccessToken, makeBatchRequest, sendRequest } from './index.js'
import axios from 'axios'
import bus from '../../utils/EventBus.js'
import Event from '../../utils/EventNames.js'
import Cache from '../../cache/Cache.js'

import { getLibraryDriveFromConfig, getSiteConfig, getSiteConfigByDriveItem } from '../../community/siteProvider.js'

export const ResourceStatus = {
  DRAFT: 'DRAFT',
  APPROVED: 'APPROVED'
}

const PREVIEW_TTL_MS = 2 * 60 * 1000
const DOWNLOAD_URL_TTL_MS = 4 * 60 * 1000
const filePreviewCache = new Map()
const filePreviewInFlight = new Map()
const fileDownloadUrlCache = new Map()
const fileDownloadUrlInFlight = new Map()

const isBlobUrl = (url) => typeof url === 'string' && /^\s*blob:/i.test(url)

const getValidCachedValue = (cache, key) => {
  const cached = cache.get(key)
  if (!cached) return undefined
  if (cached.expiresAt <= Date.now()) {
    cache.delete(key)
    return undefined
  }
  return cached.value
}

const setCachedValue = (cache, key, value, ttlMs) => {
  cache.set(key, {
    value,
    expiresAt: Date.now() + ttlMs
  })
}

const getDriveItemPath = (siteId, driveId, resourceId, suffix = '') => {
  if (driveId) {
    return `/sites/${siteId}/drives/${driveId}/items/${resourceId}${suffix}`
  }
  return `/sites/${siteId}/drive/items/${resourceId}${suffix}`
}

export const getFileOpenUrl = async (file) => {
  const { siteId } = getSiteConfig()
  const webUrl = file?.driveItem?.webUrl ?? file?.webUrl

  const documentId = file?.driveItem?.id
  const driveId = file?.driveItem?.parentReference?.driveId

  const previewData = await getFileResourcePreview(documentId, siteId, driveId) //driveIds['Shared Documents'])
  
  const { getUrl: _getUrl } = previewData || {}
  if (_getUrl && !isBlobUrl(_getUrl)) return _getUrl
  if (webUrl) return webUrl

  return await getFileDownloadUrl(documentId, siteId, driveId)
}

// export const getFileViewUrl = async (resourceId) => {
//   const { siteId } = getSiteConfig()
//   const accessToken = await getAccessToken()
//   const baseUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/drive/items/${resourceId}/createLink`
//   const c = {
//     headers: {
//       Authorization: `Bearer ${accessToken}`,
//       "Content-Type": "application/json"
//     }
//   }
//   const payload = {
//     type: "view"
//   }
//   try {
//   const response = await axios.post(baseUrl, payload, c)
//   if (response.status >= 200 && response.status < 400) {
//     const {data} = response
//     return data.link?.webUrl ?? undefined
//   } else {
//     return undefined
//   }
//   } catch (error) {
//     throw new Error(error.response?.data?.error?.message ?? error.message)
//   }
  
// }

// export const getResourceTagChoices = async () => {
//   const { listIds } = getSiteConfig()
//   return await getColumnChoices(listIds.documents, 'QSEPortalTagChoices')
// }

// export const getResourceLibraryCategories = async () => {
//   const { listIds } = getSiteConfig()
//   return await getColumnChoices(listIds.documents, 'QSEPortalCategoryChoices')
// }

export const getResourceFilesByCategoryUrl = (category, batch = false, driveConfig = undefined) => {
  const { siteId, listIds } = getSiteConfig()
  const p = batch ? '' : 'https://graph.microsoft.com/v1.0'
  const listId = driveConfig ? driveConfig.listId : listIds.documentLibrary
  return `${p}/sites/${siteId}/lists/${listId}/items?$expand=DriveItem&$filter=(fields/QSEPortalResourceCategory+eq+'${encodeURIComponent(category)}'+or+fields/QSEPortalResourceCategory+eq+'category:${encodeURIComponent(category)}')+and+fields/ContentType+eq+'Document'+and+fields/Status+ne+'${ResourceStatus.DRAFT}'`
}

export const getResourceLibraryFilesByCategory = async (category, driveConfig = undefined) => {
  const accessToken = await getAccessToken()
  const url = getResourceFilesByCategoryUrl(category, false, driveConfig) //`https://graph.microsoft.com/v1.0/sites/${siteId}/lists/${listIds.documentLibrary}/items?$expand=DriveItem&$filter=(fields/QSEPortalResourceCategory+eq+'${encodeURIComponent(category)}'+or+fields/QSEPortalResourceCategory+eq+'category:${encodeURIComponent(category)}')+and+fields/ContentType+eq+'Document'+and+fields/Status+ne+'${ResourceStatus.DRAFT}'`
  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  })
  const { value } = data
  return value
}

export const getResourceLibraryFilesByCategoryBatch = async (categoriesArr, _accessToken, driveConfig = undefined) => {
  const accessToken = _accessToken ?? await getAccessToken()
  const resourceRequests = (categoriesArr ?? []).map(category => {
    return {
      id: category,
      url: getResourceFilesByCategoryUrl(category, true, driveConfig),
      method: 'GET'
    }
  })

  const batchRequests = makeBatchRequest(resourceRequests)
  
  const results = await sendRequest(batchRequests, accessToken)
  const responses = results.responses ?? []
  const validResults = responses.reduce((p, r) => {
    if (r.status >= 200 && r.status < 300) {
      return [...p, {id: r.id, body: r.body}]
    }
    return p
  }, [])
  
  return validResults
}

export const getResourceLibraryFileById = async (rId, driveConfig = undefined) => {
  const { siteId, listIds } = getSiteConfig()
  const accessToken = await getAccessToken()

  const listId = driveConfig ? driveConfig.listId : listIds.documentLibrary

  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/lists/${listId}/items/${rId}?$expand=DriveItem`
  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  })

  return data
}

const getResourceLibraryFileBatchUrl = (rId, idIsDriveItemId = false, driveConfig = undefined) => {
  const { siteId, listIds, driveIds } = getSiteConfig()
  if (idIsDriveItemId) {
    const driveId = driveConfig ? driveConfig.driveId : driveIds.documents
    return `/sites/${siteId}/drives/${driveId}/items/${rId}/listItem?$expand=DriveItem`
  }
  const listId = driveConfig ? driveConfig.listId : listIds.documentLibrary
  return `/sites/${siteId}/lists/${listId}/items/${rId}?$expand=DriveItem`
}

export const getResourceLibraryFiles = async (fileDataArr, _accessToken = undefined, idsAreDriveIds = false, driveConfig = undefined) => {
  const accessToken = _accessToken ?? await getAccessToken()
  const resourceRequests = (fileDataArr ?? []).map(item => {
    const id = typeof item === "string" ? item : item?.id
    if (!driveConfig && typeof item === "object") {
      const siteConfig = getSiteConfigByDriveItem(item)
      driveConfig = getLibraryDriveFromConfig(siteConfig, item.parentReference.driveId)
    }
    return {
      id,
      url: getResourceLibraryFileBatchUrl(id, idsAreDriveIds, driveConfig),
      method: 'GET'
    }
  })

  const batchRequests = makeBatchRequest(resourceRequests)
  const results = await sendRequest(batchRequests, accessToken)
  const responses = results.responses ?? []
  const validResults = responses.reduce((p, r) => {
    if (r.status >= 200 && r.status < 300) {
      return [...p, r.body]
    }
    return p
  }, [])

  return [...validResults]
}

export const getSiteSettings = async (refreshCache = false, driveConfig = undefined) => {
  const { settingsFilePath } = getSiteConfig()
  const cacheKey = 'site-settings'
  if (!refreshCache) {
    const cacheHit = Cache.getItem(cacheKey)
    if(cacheHit) return cacheHit
  }
  const file = await getResourceLibraryFileByPath(settingsFilePath, driveConfig)
  let result = {}
  if(typeof file === 'string') {
    result = JSON.parse(file)
  } else {
    result = file
  }
  Cache.setItem(cacheKey, result)
  return result
}

export const getResourceLibraryFileByPath = async (filePath, driveConfig = undefined) => {
  const { siteId, driveIds } = getSiteConfig()
  const accessToken = await getAccessToken();

  const driveId = driveConfig ? driveConfig.driveId : driveIds.documents

  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/root:/${encodeURIComponent(filePath)}`;
  
  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  const stuff = await axios.get(data['@microsoft.graph.downloadUrl'])
  return stuff.data;
};

export const uploadFileToPath = async (
  filePathRelative,
  fileContent,
  contentType = 'application/octet-stream',
  driveConfig = undefined
) => {
  const { siteId, driveIds } = getSiteConfig()
  const accessToken = await getAccessToken();

  const driveId = driveConfig ? driveConfig.driveId : driveIds.documents
  
  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/root:/${filePathRelative}:/content`;
  
  const { data } = await axios.put(url, fileContent, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': contentType
    }
  });

  return data; // Graph returns file metadata
};

export const getResourceLibraryFilesByTag = async (tags, driveConfig = undefined) => {
  const { urls } = getSiteConfig()
  const accessToken = await getAccessToken()
  const driveName = driveConfig ? driveConfig.name : 'Shared Documents'

  const tagsSearchTerms = tags.includes(',') ? tags.split(',').map(t => `"${t}"`).join(' ') : `"${tags}"`
  const searchUrl = `https://graph.microsoft.com/v1.0/search/query`
  const searchBody = {
    "requests": [
      {
        "entityTypes": [
          "driveItem"
        ],
        "query": {
          "queryString": `${tagsSearchTerms} path: "${urls.baseSite}/${driveName}" AND ParentLink:"/Published"`
        }
      }
    ]
  }
  const { data: searchData } = await axios.post(searchUrl, searchBody, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  })

  const searchValue = searchData.value[0]

  const idArr = (searchValue?.hitsContainers[0].hits ?? []).map(h => h.resource.listItem.id)
  return await getResourceLibraryFiles(idArr, accessToken, false, driveConfig)
}

export const getItemAnalytics = async (itemId, timeframe = 'allTime') => {
  const { siteId, listIds } = getSiteConfig()
  const accessToken = await getAccessToken()
  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/lists/${listIds.documentLibrary}/items/${itemId}/analytics/${timeframe}`
  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  })
  const { value } = data
  return value
}

export const getItemAnalyticsBatch = async (itemIdList, timeframe = 'allTime') => {
  const { siteId, listIds } = getSiteConfig()
  const accessToken = await getAccessToken()
  const url = `https://graph.microsoft.com/v1.0/$batch`

  const requests = itemIdList.map(i => {
    return {
      id: i,
      method: 'GET',
      url: `/sites/${siteId}/lists/${listIds.documentLibrary}/items/${i}/analytics/${timeframe}`
    }
    //https://graph.microsoft.com/v1.0
  })

  const requestChunks = ((arr, chunkSize) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += chunkSize) {
      const chunk = arr.slice(i, i + chunkSize);
      chunks.push(chunk);
    }
    return chunks;
  })(requests, 20)

  let allResponses = []
  for (const chunk of requestChunks) {
    const { data } = await axios.post(url, { requests: chunk }, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": 'application/json',
        "Accept": 'application/json'
      }
    })
    allResponses = [...allResponses, ...data.responses]
  }
  return allResponses
}

export const getResourceLibraryFilesInStatus = async (status, driveConfig = undefined) => {
  const { siteId, listIds } = getSiteConfig()
  const accessToken = await getAccessToken()

  const listId = driveConfig ? driveConfig.listId : listIds.documentLibrary

  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/lists/${listId}/items?$expand=DriveItem&$filter=fields/Status+eq+'${status}'+and+fields/ContentType+eq+'Document'`
  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  })
  const { value } = data
  return value
}

export const publishResourceLibraryDraft = async (resourceId, driveConfig = undefined) => {
  const { siteId, driveIds } = getSiteConfig()
  const accessToken = await getAccessToken()

  const driveId = driveConfig ? driveConfig.driveId : driveIds.documents

  const gUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/items/root:/Published`
  const gResponse = await axios.get(gUrl, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json"
    }
  })

  const { data: publishedFolder } = gResponse

  const publishUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/items/${resourceId}`
  let patchBody = {
    "Status": ResourceStatus.APPROVED
  }
  let patchResponse = await axios.patch(publishUrl + '/listItem/fields', patchBody, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json"
    }
  })

  // Move the file to Published folder
  patchBody = {
    "parentReference": {
      "id": publishedFolder.id
    }
  }
  patchResponse = await axios.patch(publishUrl, patchBody, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json"
    }
  })



}

export const getResourceLibraryFilesForIdentity = async ({ email, username, uniqueId }, folder, driveConfig = undefined) => {
  const { siteId, driveIds } = getSiteConfig()
  const accessToken = await getAccessToken()
  const driveId = driveConfig ? driveConfig.driveId : driveIds.documents
  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/root:/${folder}:/children?$expand=listItem`
  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  })
  const { value } = data

  const normalize = (v) => String(v ?? '').trim().toLowerCase()
  const identityKeys = new Set([
    normalize(email),
    normalize(username),
    normalize(uniqueId)
  ].filter(Boolean))

  //restructure to ListItem response form
  const list = value.reduce((p, d) => {
    const contentType = String(d?.listItem?.fields?.ContentType ?? d?.listItem?.contentType?.name ?? '').trim().toLowerCase()
    if (contentType !== 'document') return p

    const createdByUser = d?.createdBy?.user ?? {}
    const createdByCandidates = [
      normalize(createdByUser.email),
      normalize(createdByUser.userPrincipalName),
      normalize(createdByUser.displayName),
      normalize(createdByUser.id)
    ]

    if (createdByCandidates.some((c) => identityKeys.has(c))) {
      return [...p, { ...(structuredClone(d.listItem)), driveItem: structuredClone(d) }]
    }
    return p
  }, [])
  return list
}

export const checkFilenameConflict = async (folder, filename, driveConfig = undefined) => {
  const { siteId, driveIds } = getSiteConfig()
  const accessToken = await getAccessToken()
  const driveId = driveConfig ? driveConfig.driveId : driveIds.documents
  let url = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/root:/${folder}:/children?$select=name`

  let conflict = false
  const c = {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  }
  do {
    let searchResponse = await axios.get(url, c)
    const { value: files } = searchResponse.data
    for (const file of files) {
      if (file.name.toLowerCase() === filename.toLowerCase()) {
        conflict = true
        break
      }
    }
    url = searchResponse.data['@odata.nextLink']
  } while (url !== undefined)

  return conflict
}

const getResourceUploadUrl = async (fileProperties, driveConfig = undefined) => {
  const { siteId, driveIds } = getSiteConfig()
  const accessToken = await getAccessToken()
  const driveId = driveConfig ? driveConfig.driveId : driveIds.documents
  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/items/root:/Submitted/${encodeURIComponent(fileProperties.fileName)}:/createUploadSession`
  const postBody = {
    "item": {
      "@microsoft.graph.conflictBehavior": "rename",
      "name": `${fileProperties.fileName}`
    }
  }
  const { status, data } = await axios.post(url, postBody, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  })

  const { uploadUrl, expirationDateTime } = data
  return { uploadUrl, expirationDateTime }
}

function getUploadRanges(fileLength, chunkSize = (30 * 320 * 1024)) {
  const ranges = []

  if (fileLength <= chunkSize) {
    ranges.push({ start: 0, end: fileLength - 1, size: fileLength })
  } else {
    let start = 0
    while (start < fileLength) {
      const end = Math.min(start + chunkSize - 1, fileLength - 1)
      const size = end - start + 1
      ranges.push({ start, end, size })
      start = end + 1
    }
  }

  return ranges
}

export const updateFileResourceProperties = async (resourceId, fileProperties, asDraft = true, driveConfig = undefined) => {
  const { siteId, driveIds } = getSiteConfig()
  const accessToken = await getAccessToken()
  const driveId = driveConfig ? driveConfig.driveId : driveIds.documents
  const baseUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/items/${resourceId}`
  let patchBody = {
    "description": fileProperties.description,
    "title": fileProperties.title,
  }
  let patchResponse = await axios.patch(baseUrl, patchBody, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json"
    }
  })

  patchBody = {
    "QSEPortalResourceCategory": fileProperties.category.includes('category:') ? fileProperties.category : `category:${fileProperties.category}`
  }
  if (asDraft) {
    patchBody["Status"] = ResourceStatus.DRAFT
  }
  const tags = fileProperties.tags ?? []
  if (tags.length > 0) {
    patchBody['QSEPortalTags'] = tags.map(t => {
      if (!t.includes('tag:')) return `tag:${t}`
      return t
    }).join(',')
  } else {
    patchBody['QSEPortalTags'] = null
  }
  patchResponse = await axios.patch(baseUrl + '/listItem/fields', patchBody, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json"
    }
  })
  return patchResponse?.data
}

const uploadRangeChunk = async (chunkInfo) => {
  const {uploadUrl, size, start, end, blob, fileSize, attempt, expirationDateTime} = chunkInfo
  let response, uploadStatus
  try {
    response = await axios.put(uploadUrl, blob, {
      headers: {
        "Content-Length": size,
        "Content-Range": `bytes ${start}-${end}/${fileSize}`
      }
    })
    uploadStatus = 'success'
  } catch (error) {
    uploadStatus = 'error'
    console.error(error)
    const delay = Math.abs(Math.pow(1.5, attempt) * 1000); 
    bus.emit(Event.ALERT, {
      "text": `Error uploading, retrying in ${delay / 1000} seconds. Expiration: ${expirationDateTime}`,
      "style": "error",
      "duration": delay,
    })
    await new Promise(resolve => setTimeout(resolve, delay))
  }

  return {
    response,
    uploadStatus
  }
}

export const conductFileResourceUpload = async (fileProperties, targetFile, progressEvent = undefined, asDraft = true, driveConfig = undefined) => {
  let resourceId = undefined
  const ranges = getUploadRanges(fileProperties.fileSize)
  const conductSize = 3 + ranges.length
  let progress = 0
  const reportProgress = () => {
    if (progressEvent) {
      bus.emit(progressEvent, {
        completed: conductSize,
        progress: ++progress
      })
    }
  }

  try {
    reportProgress()
    const { uploadUrl, expirationDateTime } = await getResourceUploadUrl(fileProperties, driveConfig)
    const _expirationDateTime = new Date(expirationDateTime).getTime()

    reportProgress()
    let completedResponse
    for (const r of ranges) {
      const size = r.size
      const start = r.start
      const end = r.end
      const blob = targetFile.slice(start, end + 1)
      
      let attempt = 0
      let chunkUploadStatus
      do {
        attempt++
        const chunkInfo = {uploadUrl, size, start, end, blob, fileSize: fileProperties.fileSize, attempt, expirationDateTime}
        const { response, uploadStatus } = await uploadRangeChunk(chunkInfo)
        chunkUploadStatus = uploadStatus
        if (uploadStatus === 'success') {
          completedResponse = response
        }
      } while ( attempt < 11 && chunkUploadStatus === 'error' && new Date().getTime() < _expirationDateTime );

      // completedResponse = await axios.put(uploadUrl, blob, {
      //   headers: {
      //     "Content-Length": size,
      //     "Content-Range": `bytes ${start}-${end}/${fileProperties.fileSize}`
      //   }
      // })


      reportProgress()
    }
    const uploadResponseData = completedResponse?.data
    const { id: newResourceId } = uploadResponseData    
    const resourcePropertyUpdate = await updateFileResourceProperties(newResourceId, fileProperties, asDraft, driveConfig)
    resourceId = newResourceId
    reportProgress()
  } catch (error) {
    bus.emit(Event.ALERT, {
      "text": error.message,
      "style": "error",
      "duration": 10000,
    })
  }
  return resourceId
}

export const getFileResourcePreview = async (resourceId, _siteId = undefined, _driveId = undefined) => {
  if (!resourceId) return null

  const { siteId: defaultSiteId } = getSiteConfig()
  const siteId = _siteId || defaultSiteId
  const cacheKey = `preview:${siteId}:${_driveId || 'default'}:${resourceId}`

  const cached = getValidCachedValue(filePreviewCache, cacheKey)
  if (cached) return cached

  if (filePreviewInFlight.has(cacheKey)) {
    return await filePreviewInFlight.get(cacheKey)
  }

  const promise = sendRequest({
    baseUrl: 'https://graph.microsoft.com/v1.0',
    path: getDriveItemPath(siteId, _driveId, resourceId, '/preview'),
    method: 'POST',
    body: {}
  })
    .then((data) => {
      if (data?.getUrl && isBlobUrl(data.getUrl)) {
        return { ...data, getUrl: null }
      }
      setCachedValue(filePreviewCache, cacheKey, data, PREVIEW_TTL_MS)
      return data
    })
    .finally(() => {
      filePreviewInFlight.delete(cacheKey)
    })

  filePreviewInFlight.set(cacheKey, promise)
  return await promise
}

export const getFileDownloadUrl = async (resourceId, _siteId = undefined, _driveId = undefined) => {
  if (!resourceId) return null

  const { siteId: defaultSiteId } = getSiteConfig()
  const siteId = _siteId || defaultSiteId
  const cacheKey = `download:${siteId}:${_driveId || 'default'}:${resourceId}`

  const cached = getValidCachedValue(fileDownloadUrlCache, cacheKey)
  if (cached) return cached

  if (fileDownloadUrlInFlight.has(cacheKey)) {
    return await fileDownloadUrlInFlight.get(cacheKey)
  }

  const promise = sendRequest({
    baseUrl: 'https://graph.microsoft.com/v1.0',
    path: getDriveItemPath(siteId, _driveId, resourceId),
    method: 'GET'
  })
    .then((data) => {
      // This is a pre-authenticated download URL that lasts only a short time.
      const url = data?.['@microsoft.graph.downloadUrl']
      if (url) {
        setCachedValue(fileDownloadUrlCache, cacheKey, url, DOWNLOAD_URL_TTL_MS)
      }
      return url || null
    })
    .finally(() => {
      fileDownloadUrlInFlight.delete(cacheKey)
    })

  fileDownloadUrlInFlight.set(cacheKey, promise)
  return await promise
};

const deleteResource = async (resourceId, isPublished, driveConfig = undefined) => {
  const { siteId, driveIds } = getSiteConfig()
  const accessToken = await getAccessToken()
  const driveId = driveConfig ? driveConfig.driveId : driveIds.documents
  const baseUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/items/${resourceId}`

  const c = {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json"
    }
  }

  if (isPublished) {
    await axios.delete(baseUrl, c)
  } else {
    await axios.post(baseUrl + '/permanentDelete', {}, c)
  }
}

export const deleteDraftResource = async (resourceId, driveConfig = undefined) => {
  await deleteResource(resourceId, false, driveConfig)
}

export const deletePublishedResource = async (resourceId, driveConfig = undefined) => {
  await deleteResource(resourceId, true, driveConfig)
}